import React, { useEffect, useState } from 'react'
import Image from '@corratech/pylot-image'
import { AlfaRichContent as RichContent } from '@components/alfakher/pylot-rich-content'
import { Button } from '@corratech/pylot-ui'
import { FlavoursTileProps } from '../../lib/type'
import { QuantitySelector } from 'stores/ooka_usa_store/airglobal/pylot-custom-kit-flow/src/Components/QuantitySelector/QuantitySelector'
import { useTranslation } from 'next-i18next'
import s from '@components/common/ProductTile/ProductTile.module.scss'
import ds from './Flavours.module.scss'
import cn from 'classnames'
import Price from '@corratech/pylot-custom-kit-flow/src/Components/Price/Price'
import Toast from 'stores/ooka_usa_store/airglobal/pylot-custom-kit-flow/src/Components/CenteredToast'
import { useUI } from '@corratech/pylot-ui/context'
import { useIsMobile } from '@components/common/IsMobile/useIsMobile'

export const FlavoursTile = ({
    option,
    flavour,
    selectedOptions,
    selectedFlavours,
    setSelectedOptions,
    disableAddingFlavours = false,
    numberOfAvailablePods = 1,
    isOutOfStock = false,
    selectedPods = [],
    qty: defaultQuantity = 0,
    isFavourite,
    favorite,
    setFavorite,
    mappedFlavours
}: FlavoursTileProps): JSX.Element => {
    const [openToast, setOpenToast] = useState(false)
    const { navbarScrolled } = useUI()
    const isMobile = useIsMobile()
    const { t } = useTranslation(['common', 'plp'])

    const product = option?.product
    const image = product?.thumbnail
    const [qty, setQty] = useState(defaultQuantity)

    useEffect(() => setQty(defaultQuantity), [defaultQuantity])

    const handlePodSelection = (qyantity = 0): void => {
        const isAdding = qyantity > qty
        setQty(qyantity)

        let modifiedSelectedOptions = [...selectedOptions!]
        if (qyantity === 0) {
            modifiedSelectedOptions = modifiedSelectedOptions.filter(
                (selectedOption) => !option?.uids?.includes(selectedOption)
            )
        } else {
            const uid = option?.uids?.[availablePod ?? numberOfSelectedFlavours]
            const existingUid = option?.uids?.find((uid) =>
                selectedOptions?.includes(uid)
            )
            if (isAdding) {
                modifiedSelectedOptions.push(uid)
            } else {
                modifiedSelectedOptions = modifiedSelectedOptions?.filter(
                    (uid: string): boolean => existingUid !== uid
                )
            }
        }

        if (setSelectedOptions) {
            setSelectedOptions(modifiedSelectedOptions)
        }
    }
    const currentFlavourSelected = option?.uids?.filter((uid: string) => {
        return selectedOptions?.includes(uid)
    })

    const numberOfCurrentAvailableFlavours =
        (currentFlavourSelected?.length ?? 0) - (option?.uids?.length ?? 0)
    const numberOfSelectedFlavours = selectedFlavours?.length ?? 0
    const currentAvailablePods =
        numberOfAvailablePods - numberOfSelectedFlavours
    const availablePod = option?.pods?.find(
        (pod) => !selectedPods.includes(pod)
    )
    const getButtonLabel = () => {
        if (isButtonDisabled) {
            if (isOutOfStock) {
                return <span className="text-xs">{t('SOLD OUT')}</span>
            }
            return <span className={ds['plus-icon']}> {t('+')} </span>
        }
        return t('+')
    }
    const isButtonDisabled =
        disableAddingFlavours ||
        numberOfCurrentAvailableFlavours >= currentAvailablePods ||
        !option?.pods?.includes(numberOfSelectedFlavours)! ||
        typeof availablePod == 'undefined'

    const toastText = `You have already selected ${
        option?.uids?.length ?? 0
    } pods.`

    const getButton = () => {
        return (
            <>
                <Toast
                    isOpen={openToast}
                    text={toastText}
                    sticked={navbarScrolled && isMobile}
                />
                <Button
                    type="button"
                    variant={isOutOfStock ? 'flat' : 'rounded'}
                    className={cn(ds['flavour-button'], {
                        [ds['flavour-button-OOS']]: isOutOfStock,
                        [ds['flavour-button-disabled']]: isButtonDisabled
                    })}
                    disabled={isOutOfStock}
                    onClick={() => {
                        if (isButtonDisabled) {
                            setOpenToast(true)
                            setTimeout(() => setOpenToast(false), 3000)
                            return
                        }
                        handlePodSelection(1)
                    }}
                >
                    {getButtonLabel()}
                </Button>
            </>
        )
    }
    const handleFavoriteSelection = () => {
        if (isFavourite && setFavorite && product?.sku) {
            let filteredOptions = selectedOptions
            const flavour = mappedFlavours?.find(
                (flavour) => flavour?.key == product?.sku
            )
            if (favorite) {
                const prevFlavour = mappedFlavours?.find(
                    (flavour) => flavour.key === favorite
                )
                filteredOptions = filteredOptions?.filter(
                    (option) => !prevFlavour?.uids?.includes(option)
                )
            }
            if (favorite === product.sku) {
                setFavorite(null)
            } else if (flavour && filteredOptions) {
                setFavorite(product.sku)
                filteredOptions = [...filteredOptions, ...flavour.uids]
            }
            if (setSelectedOptions) {
                setSelectedOptions([...(filteredOptions ?? [])])
            }
        }
    }
    return (
        <div
            className={cn(
                s['productitem-block'],
                ds['productitem-block'],
                isFavourite ? ds['isFavourite'] : '',
                favorite === product?.sku ? ds['active-flavour'] : ''
            )}
            tabIndex={0}
            role="button"
            onKeyDown={handleFavoriteSelection}
            onClick={handleFavoriteSelection}
        >
            <div className={cn('productimage-block')}>
                <div className={ds['filter-tittle']}>
                    {product?.ooka_product_type === 'tobacco' && 'Tobacco'}
                    {product?.ooka_product_type === 'tea' && 'Tea'}
                    {product?.ooka_product_type === 'cbd' && 'Cbd'}
                </div>
                <div
                    className={cn(
                        s['productimg-link'],
                        ds['productimg-link'],
                        'product-card-image-swipe',
                        'slider-product-img'
                    )}
                >
                    <Image
                        src={image?.url!}
                        alt={option?.product?.name!}
                        width={390}
                        height={390}
                        className={s['product-img']}
                    />
                </div>
            </div>
            <div
                className={cn(
                    s['plp-prod-details'],
                    'sliderproduct-detail',
                    ds['plp-prod-details']
                )}
            >
                <p className={cn(ds['product-name'])} title={product?.name!}>
                    {product?.name}
                </p>
                <p className={cn(ds['short-description'])}>
                    <RichContent
                        html={option?.product?.short_description?.html}
                    />
                </p>
            </div>
            <div>
                <Price product={option?.product!} />
            </div>
            {!isFavourite ? (
                <div className={ds['button-container']}>
                    {!selectedOptions?.find((selectedOption) =>
                        option?.uids?.includes(selectedOption)
                    ) ? (
                        getButton()
                    ) : (
                        <div className="pdp-qty">
                            <QuantitySelector
                                slots={option?.uids?.length}
                                quantity={qty}
                                // @ts-ignore this is added as store folders are not tracked by ts
                                isAddingDisabled={
                                    (disableAddingFlavours &&
                                        option?.uids?.length >=
                                            numberOfAvailablePods) ||
                                    disableAddingFlavours ||
                                    qty >= option?.uids?.length ||
                                    typeof availablePod == 'undefined'
                                }
                                // @ts-ignore this is added as store folders are not tracked by ts
                                customMin
                                min={0}
                                setQuantity={handlePodSelection}
                            />
                        </div>
                    )}
                </div>
            ) : (
                ''
            )}
        </div>
    )
}

export default FlavoursTile
